import { Box, Paper, UnstyledButton } from '@mantine/core';
import { AnimatePresence, motion } from 'framer-motion';
import type { FC, PropsWithChildren } from 'react';
import { useHostContext } from '@/context/HostContext.tsx';
import { type StepperSetupStep, StepperSetupStepStatus } from '@/stepper';
import { cn } from '@/utils';
import { CLASS_NAMES } from './classNames';
import StepperStepCardHeaderContent from './components/StepperStepCardHeaderContent';

type StepperStepCardProps = PropsWithChildren<{
	isAgentProgressActive: boolean;
	isContentDimmed?: boolean;
	isDisabled?: boolean;
	isLocked?: boolean;
	number: number;
	onSelect: (step: StepperSetupStep) => void;
	status: StepperSetupStepStatus;
	step: StepperSetupStep;
}>;

const StepperStepCard: FC<StepperStepCardProps> = ({
	children,
	isDisabled = false,
	isAgentProgressActive,
	isContentDimmed = false,
	isLocked = false,
	number,
	onSelect,
	status,
	step
}) => {
	const { localization } = useHostContext();
	const isActive = status === StepperSetupStepStatus.Active;
	const isCompleted = status === StepperSetupStepStatus.Completed;

	const handleSelect = () => {
		onSelect(step);
	};

	const title = localization.translate(
		'creator',
		`stepperOnboarding.steps.${step.key}.title`
	);

	const headerContent = (
		<StepperStepCardHeaderContent
			isAgentProgressActive={isAgentProgressActive}
			isCompleted={isCompleted}
			isDisabled={isDisabled}
			isLocked={isLocked}
			number={number}
			status={status}
			stepKey={step.key}
			title={title}
		/>
	);

	return (
		<Paper
			className={cn(CLASS_NAMES.stepCard, {
				'pb-0': isActive
			})}
			component="section"
			data-active={isActive}
			data-disabled={isDisabled}
			data-status={status}
			data-testid={`stepper-step-card-${step.key}`}
			shadow="none"
		>
			<Box
				className={cn(CLASS_NAMES.stepCardContent, {
					[CLASS_NAMES.stepCardContentDimmed]: isContentDimmed
				})}
				data-testid={`stepper-step-card-shell-${step.key}`}
			>
				{isActive ? (
					<Box className={cn(CLASS_NAMES.stepHeader, { 'pb-2': isActive })}>
						{headerContent}
					</Box>
				) : (
					<UnstyledButton
						aria-expanded={isActive}
						className={CLASS_NAMES.stepHeader}
						data-testid={`stepper-step-card-header-${step.key}`}
						disabled={isLocked || isDisabled}
						onClick={handleSelect}
					>
						{headerContent}
					</UnstyledButton>
				)}
				<AnimatePresence initial={false}>
					{isActive && (
						<motion.div
							animate={{ height: 'auto', opacity: 1 }}
							data-testid={`stepper-step-card-content-${step.key}`}
							exit={{ height: 0, opacity: 0 }}
							initial={{ height: 0, opacity: 0 }}
							transition={{ duration: 0.22, ease: 'easeOut' }}
						>
							{children}
						</motion.div>
					)}
				</AnimatePresence>
			</Box>
		</Paper>
	);
};

export default StepperStepCard;
